import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import HomeworkSubmitDialog from "@/components/HomeworkSubmitDialog";
import { renderCertificateHTML, getTheme } from "@/lib/certificateTemplates";
import {
  BookOpen, ClipboardText, Certificate, CurrencyDollar, ChatCircle, Megaphone,
  CheckCircle, XCircle, Clock, Star, DownloadSimple, UploadSimple, Check,
  GraduationCap, TrendUp, Warning,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

const attIcon = { present: CheckCircle, absent: XCircle, late: Clock, excused: Clock };

function StudentBoard({ bundle, isParent, onSubmit, uiLang }) {
  const { t } = useTranslation();
  const s = bundle.student;
  const summary = bundle.attendance_summary || {};
  const total = (summary.present || 0) + (summary.absent || 0) + (summary.late || 0) + (summary.excused || 0);
  const rate = total ? Math.round(((summary.present || 0) / total) * 100) : 0;
  const overdue = (bundle.invoices || []).filter((i) => i.status === "overdue").length;
  const pendingHw = (bundle.homework || []).filter((h) => !h.is_done).length;

  return (
    <div className="space-y-6" data-testid={`student-board-${s.id}`}>
      {/* KPI band */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: t("portal.kpi.attendance"), value: `${rate}%`, icon: ClipboardText, tone: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
          { label: t("portal.kpi.exams"), value: bundle.exams?.length || 0, icon: Star, tone: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
          { label: t("portal.kpi.pendingHw"), value: pendingHw, icon: BookOpen, tone: "bg-sky-500/10 text-sky-600 dark:text-sky-400" },
          { label: t("portal.kpi.overdue"), value: overdue, icon: Warning, tone: overdue > 0 ? "bg-destructive/10 text-destructive" : "bg-secondary/40 text-muted-foreground" },
        ].map((k) => (
          <Card key={k.label} className="border-border/60" data-testid={`kpi-${k.label}`}>
            <CardContent className="p-4">
              <div className={cn("w-9 h-9 rounded-md flex items-center justify-center", k.tone)}>
                <k.icon size={18} weight="duotone" />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-3">{k.label}</div>
              <div className="text-2xl font-bold font-mono mt-0.5">{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="homework">
        <TabsList className="flex-wrap">
          <TabsTrigger value="homework" data-testid="portal-tab-homework"><BookOpen size={14} className="mr-1.5" /> {t("portal.tabs.homework")}</TabsTrigger>
          <TabsTrigger value="exams" data-testid="portal-tab-exams"><Star size={14} className="mr-1.5" /> {t("portal.tabs.exams")}</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="portal-tab-attendance"><ClipboardText size={14} className="mr-1.5" /> {t("portal.tabs.attendance")}</TabsTrigger>
          {isParent && <TabsTrigger value="payments" data-testid="portal-tab-payments"><CurrencyDollar size={14} className="mr-1.5" /> {t("portal.tabs.payments")}</TabsTrigger>}
          <TabsTrigger value="certificates" data-testid="portal-tab-certs"><Certificate size={14} className="mr-1.5" /> {t("portal.tabs.certificates")}</TabsTrigger>
        </TabsList>

        {/* Homework */}
        <TabsContent value="homework" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(bundle.homework || []).length === 0 && <p className="text-sm text-muted-foreground">{t("portal.homework.empty")}</p>}
            {(bundle.homework || []).map((h) => {
              const done = h.is_done || !!h.submission;
              return (
                <Card key={h.id} className="border-border/60 card-hover"><CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>{h.title}</h3>
                    <Badge variant="outline" className="text-[10px] font-mono">{t("portal.homework.due", { date: h.due_date })}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{h.description}</p>
                  {h.submission?.grade && (
                    <div className="mt-3 p-2 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs">
                      <span className="font-bold">{t("portal.homework.gradeLabel")}: {h.submission.grade}</span>
                      {h.submission.feedback && <span className="ml-2 opacity-80">— {h.submission.feedback}</span>}
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      {t("portal.homework.completedCount", { done: h.completed_by?.length || 0, total: h.student_ids?.length || 0 })}
                    </div>
                    {done ? (
                      <Badge className="bg-emerald-500 text-white gap-1"><Check size={10} weight="bold" /> {t("portal.homework.submitted")}</Badge>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => onSubmit(h, s.id)}
                        data-testid={`hw-submit-${h.id}`} className="gap-1.5">
                        <UploadSimple size={12} weight="bold" /> {t("portal.homework.submit")}
                      </Button>
                    )}
                  </div>
                </CardContent></Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Exams */}
        <TabsContent value="exams" className="mt-4">
          <Card className="border-border/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="exams-table">
                <thead className="bg-secondary/40 border-b border-border">
                  <tr className="text-left">
                    {[t("portal.exams.exam"), t("portal.exams.date"), t("portal.exams.score"), t("portal.exams.grade"), t("portal.exams.feedback")].map((h, i) =>
                      <th key={i} className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(bundle.exams || []).length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">{t("common.noData")}</td></tr>
                  )}
                  {(bundle.exams || []).map((e) => (
                    <tr key={e.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 font-semibold">{e.exam_name}</td>
                      <td className="px-4 py-3 font-mono text-xs">{e.date}</td>
                      <td className="px-4 py-3 font-mono font-bold">{e.score}/{e.max_score}</td>
                      <td className="px-4 py-3">
                        <Badge className={cn(
                          e.grade === "A" ? "bg-emerald-500 text-white" :
                          e.grade === "B" ? "bg-sky-500 text-white" :
                          e.grade === "C" ? "bg-amber-500 text-white" : "bg-destructive text-destructive-foreground"
                        )}>{e.grade}</Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{e.feedback}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Attendance */}
        <TabsContent value="attendance" className="mt-4">
          <Card className="border-border/60"><CardContent className="p-5">
            <div className="grid grid-cols-4 gap-2 mb-4">
              {["present", "absent", "late", "excused"].map((k) => (
                <div key={k} className="p-3 rounded-md bg-secondary/40 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t(`portal.attendance.status.${k}`)}</div>
                  <div className="text-xl font-bold font-mono mt-1">{summary[k] || 0}</div>
                </div>
              ))}
            </div>
            {(bundle.attendance || []).length === 0 ? <p className="text-sm text-muted-foreground">{t("portal.attendance.empty")}</p> :
              <ul className="space-y-2">
                {bundle.attendance.map((a) => {
                  const Icon = attIcon[a.status] || Clock;
                  return (
                    <li key={a.id} className="flex items-center justify-between p-3 rounded-md border border-border/60">
                      <div className="flex items-center gap-3">
                        <Icon size={18} weight="duotone" className={cn(
                          a.status === "present" && "text-emerald-500",
                          a.status === "absent" && "text-destructive",
                          a.status === "late" && "text-amber-500",
                          a.status === "excused" && "text-sky-500",
                        )} />
                        <div className="text-xs text-muted-foreground font-mono">{a.date}</div>
                      </div>
                      <Badge variant="outline">{t(`portal.attendance.status.${a.status}`, { defaultValue: a.status })}</Badge>
                    </li>
                  );
                })}
              </ul>
            }
          </CardContent></Card>
        </TabsContent>

        {/* Payments (parent view) */}
        {isParent && (
          <TabsContent value="payments" className="mt-4">
            <Card className="border-border/60 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`payments-table-${s.id}`}>
                  <thead className="bg-secondary/40 border-b border-border">
                    <tr className="text-left">
                      {[t("portal.payments.invoice"), t("portal.payments.total"), t("portal.payments.paid"), t("portal.payments.due"), t("portal.payments.status")].map((h, i) =>
                        <th key={i} className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {(bundle.invoices || []).length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">{t("common.noData")}</td></tr>
                    )}
                    {(bundle.invoices || []).map((inv) => (
                      <tr key={inv.id} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 font-mono text-xs">{inv.invoice_no}</td>
                        <td className="px-4 py-3 font-mono font-bold">${inv.total?.toFixed(0)}</td>
                        <td className="px-4 py-3 text-xs">{inv.installments_paid}/{inv.installments_count}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{inv.due_date}</td>
                        <td className="px-4 py-3">
                          <Badge variant={inv.status === "paid" ? "default" : inv.status === "overdue" ? "destructive" : "secondary"}>{t(`payments.status.${inv.status}`, { defaultValue: inv.status })}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        )}

        {/* Certificates */}
        <TabsContent value="certificates" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(bundle.certificates || []).length === 0 && <p className="text-sm text-muted-foreground">{t("portal.certificates.empty")}</p>}
            {(bundle.certificates || []).map((c) => {
              const theme = getTheme(c.template);
              return (
                <Card key={c.id} data-testid={`cert-card-${c.id}`}
                  className="border-border/60 card-hover overflow-hidden"
                  style={{ borderColor: theme.accent }}>
                  <div className="h-2" style={{ background: `linear-gradient(90deg, ${theme.accent}, ${theme.accentLight})` }} />
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-14 h-14 rounded-md flex items-center justify-center text-2xl shrink-0 border-2"
                        style={{
                          background: `linear-gradient(135deg, ${theme.bgFrom}, ${theme.bgTo})`,
                          borderColor: theme.accent,
                          color: theme.textOn,
                        }}>
                        {theme.seal}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="capitalize text-[10px] font-bold" style={{ backgroundColor: theme.accent, color: theme.bgFrom }}>
                            {theme.label} · {theme.tag}
                          </Badge>
                          {c.level && <Badge variant="outline" className="font-mono text-[10px]">{c.level}</Badge>}
                          {c.language && (
                            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">{c.language}</Badge>
                          )}
                        </div>
                        <div className="font-semibold mt-2" style={{ fontFamily: "Manrope" }}>{c.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{s.full_name}</div>
                        <div className="text-[10px] text-muted-foreground font-mono mt-1">{c.certificate_no}</div>
                      </div>
                    </div>
                    <Button
                      data-testid={`cert-download-${c.id}`}
                      variant="outline" size="sm" className="w-full mt-3 gap-2"
                      onClick={async () => {
                        let signatureUrl = "";
                        let directorName = "";
                        try {
                          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings-public/director_signature`).then((x) => x.json());
                          signatureUrl = r?.value || "";
                          const n = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings-public/director_name`).then((x) => x.json());
                          directorName = n?.value || "Salih Bildik";
                        } catch { directorName = "Salih Bildik"; }
                        const html = await renderCertificateHTML(c, s.full_name, { signatureUrl, directorName, uiLang });
                        const w = window.open("", "_blank");
                        if (!w) return;
                        w.document.write(html); w.document.close();
                      }}
                    >
                      <DownloadSimple size={14} /> {t("portal.certificates.download", { tier: theme.label })}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function Portal() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isParent = user?.role === "parent";
  const [data, setData] = useState(null);
  const [hwPick, setHwPick] = useState(null); // { hw, studentId }
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [{ data: portal }, { data: m }] = await Promise.all([
        api.get("/portal/me"),
        api.get("/messages"),
      ]);
      setData(portal);
      setMessages(m);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const students = data?.students || [];
  const announcements = data?.announcements || [];
  const pinned = announcements.filter((a) => a.pinned);
  const [activeStudent, setActiveStudent] = useState("");
  useEffect(() => {
    if (students.length && !activeStudent) setActiveStudent(students[0].student.id);
  }, [students, activeStudent]);

  const currentBundle = useMemo(
    () => students.find((s) => s.student.id === activeStudent) || students[0],
    [students, activeStudent],
  );

  if (loading) return <div className="text-sm text-muted-foreground p-6">{t("common.loadingDots")}</div>;

  if (!students.length) {
    return (
      <Card className="border-border/60"><CardContent className="p-8 text-center space-y-2">
        <GraduationCap size={40} className="mx-auto text-muted-foreground" />
        <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>{t("portal.noLink.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("portal.noLink.body")}</p>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
          {isParent ? t("portal.roleHeader.parent") : t("portal.roleHeader.student")}
        </div>
        <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>
          {t("portal.hello", { name: user?.name || "" })}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isParent ? t("portal.subtitleParent") : t("portal.subtitleStudent")}
        </p>
      </div>

      {/* Pinned announcements */}
      {pinned.length > 0 && (
        <Card className="border-brand-red bg-destructive/5"><CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Megaphone size={18} weight="duotone" className="text-destructive" />
            <h3 className="font-semibold" style={{ fontFamily: "Manrope" }}>{t("portal.pinnedTitle")}</h3>
          </div>
          <ul className="space-y-2">
            {pinned.map((a) => (
              <li key={a.id}>
                <div className="font-semibold text-sm">{a.title}</div>
                <div className="text-xs text-muted-foreground">{a.body}</div>
              </li>
            ))}
          </ul>
        </CardContent></Card>
      )}

      {/* Parent multi-child tab picker */}
      {isParent && students.length > 1 && (
        <div className="flex gap-2 flex-wrap" data-testid="child-picker">
          {students.map((sb) => {
            const s = sb.student;
            const isActive = activeStudent === s.id;
            return (
              <button
                key={s.id}
                data-testid={`child-tab-${s.id}`}
                onClick={() => setActiveStudent(s.id)}
                className={cn(
                  "flex items-center gap-3 px-4 py-2 rounded-full text-sm font-medium border transition-all",
                  isActive
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-card border-border/60 text-muted-foreground hover:bg-secondary hover:text-foreground",
                )}
              >
                <div className="w-7 h-7 rounded-full bg-secondary/60 flex items-center justify-center overflow-hidden shrink-0">
                  {s.photo ? <img src={s.photo} alt={s.full_name} className="w-full h-full object-cover" /> :
                    <span className="text-xs font-bold">{s.full_name?.charAt(0)}</span>}
                </div>
                <div className="text-left">
                  <div className="text-sm">{s.full_name}</div>
                  <div className="text-[10px] font-mono opacity-70">{s.student_number}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {currentBundle && (
        <StudentBoard
          key={currentBundle.student.id}
          bundle={currentBundle}
          isParent={isParent}
          onSubmit={(hw, sid) => setHwPick({ hw, studentId: sid })}
          uiLang={i18n.language}
        />
      )}

      {/* Announcements + Messages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border/60"><CardContent className="p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Megaphone size={16} weight="duotone" /> {t("portal.tabs.announcements")}
          </h3>
          <div className="space-y-3">
            {announcements.length === 0 && <p className="text-sm text-muted-foreground">{t("common.noData")}</p>}
            {announcements.slice(0, 5).map((a) => (
              <div key={a.id} className={cn("p-3 rounded-md border border-border/60", a.pinned && "border-brand-red bg-destructive/5")}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">{a.title}</div>
                  {a.pinned && <Badge variant="destructive" className="text-[10px]">{t("portal.announcements.pinned")}</Badge>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{a.body}</div>
                <div className="text-[10px] text-muted-foreground mt-1.5">{t("portal.announcements.by", { author: a.author || "—" })}</div>
              </div>
            ))}
          </div>
        </CardContent></Card>

        <Card className="border-border/60"><CardContent className="p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <ChatCircle size={16} weight="duotone" /> {t("portal.tabs.messages")}
          </h3>
          <div className="space-y-2">
            {messages.length === 0 && <p className="text-sm text-muted-foreground">{t("portal.messages.empty")}</p>}
            {messages.slice(0, 5).map((m) => (
              <div key={m.id} className="p-3 rounded-md border border-border/60">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">{m.from_name}
                    <span className="text-xs text-muted-foreground font-normal"> · {t(`roles.${m.from_role}`, { defaultValue: m.from_role.replace("_", " ") })}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono">{new Date(m.created_at).toLocaleDateString()}</div>
                </div>
                <div className="text-sm font-semibold mt-1">{m.subject}</div>
                <div className="text-sm text-muted-foreground">{m.body}</div>
              </div>
            ))}
          </div>
        </CardContent></Card>
      </div>

      <HomeworkSubmitDialog
        homework={hwPick?.hw}
        studentId={hwPick?.studentId}
        open={!!hwPick}
        onOpenChange={(v) => !v && setHwPick(null)}
        onSubmitted={() => load()}
      />
    </div>
  );
}
